// @ts-ignore
import zeroPad from "zeropad";
import axios from "axios";
import { BASE_STREAM_URL } from "./type";

export const createScreenshot = (
  videoTag: HTMLVideoElement
): { blobURL: string; base64Img: string } => {
  let blobURL = "";
  let base64Img = "";

  const canvas: HTMLCanvasElement = document.createElement("canvas");

  // Set canvas size to match the video frame
  canvas.width = videoTag.videoWidth;
  canvas.height = videoTag.videoHeight;

  // Get the 2D drawing context
  const ctx = canvas.getContext("2d");
  if (ctx) {
    // Draw the current video frame onto the canvas
    ctx.drawImage(videoTag, 0, 0, canvas.width, canvas.height);

    // Convert the canvas content to a data URL (base64)
    base64Img = canvas.toDataURL("image/png");

    // Convert the canvas content to a blob and create a URL for it
    canvas.toBlob((blob) => {
      if (blob) {
        blobURL = URL.createObjectURL(blob);
      }
    }, "image/png");
  }

  return {
    blobURL,
    base64Img,
  };
};

// export function hmsToSeconds(timeString: string): number {
//   const parts = timeString.split(':').map((part) => parseInt(part, 10));

//   if (parts.some(isNaN)) return NaN;

//   if (parts.length === 3) {
//     // HH:MM:SS format
//     return parts[0] * 3600 + parts[1] * 60 + parts[2];
//   } else if (parts.length === 2) {
//     // MM:SS format
//     return parts[0] * 60 + parts[1];
//   } else if (parts.length === 1) {
//     // SS format
//     return parts[0];
//   }

//   return NaN;
// }

export function isValidTimeFormat(str: string) {
  if (!str) return false;
  // Only digits and colons, no leading/trailing/multiple colons
  if (!/^\d{1,4}(:\d{1,2}){0,2}$/.test(str)) return false;
  const parts = str.split(":");
  if (parts.length > 3) return false;
  // 1 segment: seconds (0-59 or any, but let's allow any non-negative integer for seconds)
  if (parts.length === 1) {
    return /^\d+$/.test(parts[0]);
  }
  // 2 segments: minutes:seconds (0-59 for seconds)
  if (parts.length === 2) {
    if (!/^\d+$/.test(parts[0]) || !/^\d{1,2}$/.test(parts[1])) return false;
    if (Number(parts[1]) > 59) return false;
    return true;
  }
  // 3 segments: hours:minutes:seconds (0-59 for minutes/seconds)
  if (parts.length === 3) {
    if (
      !/^\d+$/.test(parts[0]) ||
      !/^\d{1,2}$/.test(parts[1]) ||
      !/^\d{1,2}$/.test(parts[2])
    )
      return false;
    if (Number(parts[1]) > 59 || Number(parts[2]) > 59) return false;
    return true;
  }
  return false;
}

// Convert valid time string to seconds
export function hmsToSeconds(hms: string) {
  if (!isValidTimeFormat(hms)) return NaN;
  const parts = hms.split(":").map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  } else if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  } else if (parts.length === 1) {
    return parts[0];
  }
  return 0;
}

export function secondsToMs(d: number): string {
  d = Number(d);
  const m = Math.floor((d % 3600) / 60);
  const s = Math.floor((d % 3600) % 60);

  const mDisplay = m > 0 ? zeroPad(m) + ":" : "00:";
  const sDisplay = s > 0 ? zeroPad(s) + "" : "00";
  return mDisplay + sDisplay;
}

export function secondsToHms(d: number): string {
  d = Number(d);
  const h = Math.floor(d / 3600);
  const m = Math.floor((d % 3600) / 60);
  const s = Math.floor((d % 3600) % 60);

  if (h > 0) {
    // Show hours only if there are hours
    const hDisplay = h < 10 ? "0" + h + ":" : h + ":";
    const mDisplay = m > 0 ? zeroPad(m) + ":" : "00:";
    const sDisplay = s > 0 ? zeroPad(s) + "" : "00";
    return hDisplay + mDisplay + sDisplay;
  } else {
    // Show only minutes and seconds if no hours
    const mDisplay = m > 0 ? zeroPad(m) + ":" : "00:";
    const sDisplay = s > 0 ? zeroPad(s) + "" : "00";
    return mDisplay + sDisplay;
  }
}
export function msToHms(d: number): string {
  return secondsToHms(d / 1000);
}

interface HLSResponse {
  url: string;
  isLive: boolean;
  error: string | null;
}

export const fetchSlikeIdHLS = async (
  slikeId: string
): Promise<HLSResponse> => {
  const url = `${BASE_STREAM_URL}/${slikeId.substring(
    2,
    4
  )}/${slikeId.substring(4, 6)}/${slikeId}/${slikeId}.json`;

  try {
    const { data } = await axios.get(url, {
      withCredentials: false,
    });

    if (data?.flavors && data.flavors.length) {
      const hlsFlavor = data.flavors.find(
        (flavor: any) => flavor.type === "hls"
      );

      if (hlsFlavor) {
        const hlsUrl = hlsFlavor.url.startsWith("http")
          ? hlsFlavor.url
          : `https:${hlsFlavor.url}`;
        return { url: hlsUrl, isLive: data.isLive, error: null };
      }
    }

    return { url: "", isLive: false, error: "No HLS available" };
  } catch (error) {
    return { url: "", isLive: false, error: "Failed to fetch HLS data" };
  }
};

type Comparable =
  | string
  | number
  | boolean
  | Date
  | null
  | undefined
  | Comparable[]
  | { [key: string]: Comparable };

const isObject = (value: unknown): value is Record<string, Comparable> => {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  );
};

const isArray = (value: unknown): value is Comparable[] => {
  return Array.isArray(value);
};

const deepCompare = (initial: any, current: any): boolean => {
  if (typeof initial !== "object" || typeof current !== "object") {
    return initial === current;
  }

  if (isArray(initial) && isArray(current)) {
    if (initial.length !== current.length) return false;
    return initial.every((elem, idx) => deepCompare(elem, current[idx]));
  }

  if (isObject(initial) && isObject(current)) {
    const initialKeys = Object.keys(initial);
    const currentKeys = Object.keys(current);
    if (initialKeys.length !== currentKeys.length) return false;
    return initialKeys.every((key) => deepCompare(initial[key], current[key]));
  }
  return false;
};

export const getChangedValues = <T extends Record<string, any>>(
  initial: T,
  current: T
): any => {
  const changes: any = {};
  for (const key in current) {
    if (Object.prototype.hasOwnProperty.call(current, key)) {
      const initialVal = initial[key];
      const currentVal = current[key];
      if (!deepCompare(initialVal, currentVal)) {
        if (isObject(initialVal) && isObject(currentVal)) {
          const nestedChanges = getChangedValues(initialVal, currentVal);
          if (Object.keys(nestedChanges).length > 0) {
            changes[key] = nestedChanges as T[Extract<keyof T, string>];
          }
        } else if (isArray(initialVal) && isArray(currentVal)) {
          changes[key] = currentVal as T[Extract<keyof T, string>];
        } else {
          changes[key] = currentVal;
        }
      }
    }
  }
  return changes;
};
